import Input from '@referralexchange/rea-ui-react/src/atoms/Input';
import PropTypes from 'prop-types';
import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';
import { LocationAutocomplete } from '@referralexchange/location-input';
import { isMobile as isMobileDetect } from 'react-device-detect';
import API, { generateApiUrl } from '~/config/api';

const GOOGLE_API_KEY = 'AIzaSyAhCkJXl-CJIM1m8XplB7mOdi_YMI077e4';
const LOCATION_ACTIONS_CLASS = 'property-address__location-actions';
const LOCATION_SUBMIT_CLASS = 'property-address__location-submit';

/**
 * PropertyAddress
 *
 * Property address screen. Collects users to enter their property address.
 * Uses Google places search.
 *
 * @param {*} props
 * @returns
 */
const PropertyAddress = (props) => {
  const {
    clientType,
    propertyAddressComponents,
    propertyAddress,
    propertyUnitNumber,
    setPropertyAddress,
    setPropertyAddressComponents,
    setPropertyUnitNumber,
    nextScreen,
    prevScreen,
    claimed,
  } = props;
  const screenKey = 'PropertyAddress';

  const [needsZipFallback, setNeedsZipFallback] = useState(false);
  const locationStoreRef = useRef({});
  const locationWrapperRef = useRef(null);
  const shouldAdvanceRef = useRef(false);

  // Geocode handler
  const handleGeocode = useCallback(
    async ({ google_response, geocode_input, geocode_request_uuid }) => {
      const data = {
        google_response,
        lead_type: leadType,
        geocode_input,
        geocode_request_uuid,
      };

      return fetch(
        generateApiUrl('funnel/record_geocode'),
        API.POST_CONFIG({ data })
      );
    },
    [clientType]
  );

  // set context dependent text strings
  const heading = clientType == 'buying'
    ? 'Where are you looking to buy?'
    : 'What is your address?';
  const label = clientType == 'buying' ? 'Enter city name' : 'Enter your full address';

  const updateAddressComponents = useCallback(
    (updates) => {
      setPropertyAddressComponents((prev = {}) => {
        const next = { ...prev };
        Object.entries(updates).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            delete next[key];
          } else {
            next[key] = value;
          }
        });
        return next;
      });
    },
    [setPropertyAddressComponents],
  );

  const mapComponentUpdates = useCallback(
    (key, value) => {
      if (value === null || value === undefined || value === '') {
        return { [key]: undefined };
      }
      if (key === 'streetName') {
        return { street_name: value };
      }
      if (key === 'streetNumber') {
        return { street_number: value };
      }
      if (key === 'latLng') {
        if (Array.isArray(value) && value.length === 2) {
          return { lat_lng: `${value[0]}, ${value[1]}` };
        }
        return { lat_lng: undefined };
      }
      if (['city', 'state', 'zip', 'country', 'area'].includes(key)) {
        return { [key]: value };
      }
      return null;
    },
    [],
  );

  const handleSaveValue = useCallback(
    (key, value) => {
      locationStoreRef.current[key] = value;
      if (key === 'address' || key === 'buyer_address') {
        setPropertyAddress(value || '');
      }
      if (key === 'unitNumber') {
        setPropertyUnitNumber(value || '');
      }
      const componentUpdates = mapComponentUpdates(key, value);
      if (componentUpdates) {
        updateAddressComponents(componentUpdates);
      }
    },
    [
      mapComponentUpdates,
      setPropertyAddress,
      setPropertyUnitNumber,
      updateAddressComponents,
    ],
  );

  const handleGetValue = useCallback(
    (key) => {
      if (key === 'address' || key === 'buyer_address') {
        return propertyAddress || '';
      }
      if (['city', 'state', 'zip', 'country', 'area'].includes(key)) {
        return (
          propertyAddressComponents[key] || locationStoreRef.current[key] || ''
        );
      }
      if (key === 'streetName') {
        return propertyAddressComponents.street_name || '';
      }
      if (key === 'streetNumber') {
        return propertyAddressComponents.street_number || '';
      }
      if (key === 'latLng') {
        if (propertyAddressComponents.lat_lng) {
          return propertyAddressComponents.lat_lng
            .split(',')
            .map((coord) => parseFloat(coord.trim()))
            .filter((coord) => !Number.isNaN(coord));
        }
        return undefined;
      }
      if (key === 'unitNumber') {
        return propertyUnitNumber || '';
      }
      return locationStoreRef.current[key];
    },
    [propertyAddress, propertyAddressComponents, propertyUnitNumber],
  );

  const handleResetAddressInfo = useCallback(() => {
    locationStoreRef.current = {};
    setPropertyAddress('');
    setPropertyAddressComponents({});
  }, [setPropertyAddress, setPropertyAddressComponents]);

  // Ensure that each new place selection starts from a clean slate so
  // stale components (like a previous street number) don't linger
  const handlePlaceSelectedCallback = useCallback(
    ({ translatedAddress }) => {
      if (!translatedAddress) return;

      // Build a fresh components object using the same mapping rules as saveValue/mapComponentUpdates
      const rawUpdates = {};
      const keysToSync = [
        'city',
        'state',
        'zip',
        'country',
        'streetName',
        'streetNumber',
        'latLng',
      ];

      keysToSync.forEach((key) => {
        const value = translatedAddress[key];
        const mapped = mapComponentUpdates(key, value);
        if (mapped) {
          Object.assign(rawUpdates, mapped);
        }
      });

      setPropertyAddressComponents(() => {
        const next = {};
        Object.entries(rawUpdates).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            next[key] = value;
          }
        });
        return next;
      });

      // Also clear any transient values stored locally for this screen
      locationStoreRef.current = {};
    },
    [mapComponentUpdates, setPropertyAddressComponents],
  );

  const shouldEnableNext = useMemo(() => {
    const hasLocation = propertyAddressComponents.city !== undefined
      && propertyAddressComponents.state !== undefined;
    const hasZip = propertyAddressComponents.zip !== undefined;

    if (clientType === 'buying') {
      return needsZipFallback || hasLocation;
    }

    return needsZipFallback || (hasLocation && hasZip);
  }, [
    clientType,
    needsZipFallback,
    propertyAddressComponents.city,
    propertyAddressComponents.state,
    propertyAddressComponents.zip,
  ]);

  const nextButtonTitle = useMemo(() => {
    if (needsZipFallback) return null;

    const hasLocation = propertyAddressComponents.city !== undefined
      && propertyAddressComponents.state !== undefined;
    const hasZip = propertyAddressComponents.zip !== undefined;

    if (clientType === 'buying') {
      if (hasLocation) return null;
      return 'Please enter a city';
    }

    if (hasLocation && hasZip) return null;
    return 'Please enter a valid address';
  }, [
    clientType,
    needsZipFallback,
    propertyAddressComponents.city,
    propertyAddressComponents.state,
    propertyAddressComponents.zip,
  ]);

  const leadType = useMemo(() => {
    if (clientType === 'buying') return 'buyer';
    if (clientType === 'buying and selling') return 'both';
    return 'seller';
  }, [clientType]);

  const locationStep = clientType === 'buying' ? 'location' : 'address';

  const locationClassNames = useMemo(
    () => ({
      container: 'property-address__location-container',
      inputs: 'property-address__location-inputs',
      input: 'property-address__location-input',
      secondaryContainer: 'property-address__location-secondary',
      secondaryLabel: 'property-address__location-secondary-label',
      description: 'property-address__location-description',
      error: 'property-address__location-error',
      actions: LOCATION_ACTIONS_CLASS,
      submitButton: LOCATION_SUBMIT_CLASS,
      backButton: 'property-address__location-back',
      unitInput: 'property-address__location-unit',
      zipInputs: 'property-address__location-zip-inputs',
    }),
    [],
  );

  const locationLabels = useMemo(
    () => ({
      sellerPrimaryLabel: null,
      buyerPrimaryLabel: null,
    }),
    [],
  );

  const locationAutocompleteStyles = useMemo(
    () => ({
      container: 'property-address__autocomplete-container',
      autoComplete: 'property-address__autocomplete-input',
      suggestion: 'property-address__autocomplete-suggestion',
      poweredByGoogle: 'property-address__autocomplete-powered',
      noSuggestions: 'property-address__autocomplete-no-suggestions',
    }),
    [],
  );

  const handleLocationStepCompleted = useCallback(() => {
    if (shouldAdvanceRef.current) {
      shouldAdvanceRef.current = false;
      nextScreen({ key: screenKey });
    }
  }, [nextScreen]);

  const triggerLocationSubmit = useCallback(() => {
    if (!locationWrapperRef.current) {
      shouldAdvanceRef.current = false;
      nextScreen({ key: screenKey });
      return;
    }
    const submitButton = locationWrapperRef.current.querySelector(
      `.${LOCATION_SUBMIT_CLASS}`,
    );
    if (submitButton) {
      shouldAdvanceRef.current = true;
      submitButton.click();
    } else {
      shouldAdvanceRef.current = false;
      nextScreen({ key: screenKey });
    }
  }, [nextScreen]);

  return (
    <div className="MessageAgentForm__screen" key="address">
      <div className="MessageAgentForm__screen-heading">{heading}</div>
      <div
        className={`MessageAgentForm__address ${leadType !== 'buyer' ? '--unit' : ''
          }`}
      >
        <div
          className="PropertyAddress__autocomplete"
          ref={locationWrapperRef}
        >
          <LocationAutocomplete
            apiKey={GOOGLE_API_KEY}
            leadType={leadType}
            buyerAutofillStorageKey=""
            step={locationStep}
            defaultValue={propertyAddress || ''}
            onZipChange={setNeedsZipFallback}
            getValue={handleGetValue}
            saveValue={handleSaveValue}
            resetAddressInfo={handleResetAddressInfo}
            onPlaceSelectedCallback={handlePlaceSelectedCallback}
            onStepCompleted={handleLocationStepCompleted}
            onGeocode={handleGeocode}
            isMobileDetect={isMobileDetect}
            showBackButton={false}
            classNames={locationClassNames}
            labels={locationLabels}
            primaryAutocompleteProps={{
              placeholder: label,
              styles: locationAutocompleteStyles,
            }}
            secondaryAutocompleteProps={{
              styles: locationAutocompleteStyles,
            }}
            showNextButton={false}
          />
        </div>
        {leadType !== 'buyer' && (
          <Input
            name="unit-number"
            value={propertyUnitNumber || ''}
            onChange={(e) => handleSaveValue('unitNumber', e.target.value)}
            type="text"
            className="MessageAgentForm__address-unit"
            autoComplete="off"
            placeholder="Unit #"
            noActiveLabel
          />
        )}
      </div>
      <div className="MessageAgentForm__screen-controls">
        <button
          className="--alt"
          type="button"
          onClick={(e) => {
            const overFlowEl = e.target.closest('.MessageAgentForm');
            const overFlowElTwo = e.target.closest(
              '.message-agent-modal__body',
            );
            if (overFlowEl != undefined) {
              overFlowEl.classList.remove('--overflow');
            }
            if (overFlowElTwo != undefined) {
              overFlowElTwo.classList.remove('--overflow');
            }
            prevScreen({ key: screenKey });
          }}
        >
          Back
        </button>
        <button
          type="button"
          onClick={(e) => {
            const overFlowEl = e.target.closest('.MessageAgentForm');
            const overFlowElTwo = e.target.closest(
              '.message-agent-modal__body',
            );
            if (overFlowEl != undefined) {
              overFlowEl.classList.remove('--overflow');
            }
            if (overFlowElTwo != undefined) {
              overFlowElTwo.classList.remove('--overflow');
            }
            triggerLocationSubmit();
          }}
          disabled={!shouldEnableNext}
          title={nextButtonTitle}
        >
          Next
        </button>
      </div>
    </div>
  );
};

PropertyAddress.propTypes = {
  clientType: PropTypes.string,
  propertyAddress: PropTypes.string,
  propertyAddressComponents: PropTypes.object,
  propertyUnitNumber: PropTypes.string,
  setPropertyAddress: PropTypes.func.isRequired,
  setPropertyAddressComponents: PropTypes.func.isRequired,
  setPropertyUnitNumber: PropTypes.func.isRequired,
  nextScreen: PropTypes.func,
  prevScreen: PropTypes.func,
  claimed: PropTypes.bool.isRequired,
};

PropertyAddress.defaultProps = {
  clientType: undefined,
  propertyAddress: undefined,
  propertyAddressComponents: {},
  propertyUnitNumber: undefined,
  setPropertyAddress: () => { },
  setPropertyAddressComponents: () => { },
  setPropertyUnitNumber: () => { },
  nextScreen: () => { },
  prevScreen: () => { },
  claimed: false,
};

export default PropertyAddress;
